import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

const ThreeCanvas = () => {
  const mountRef = useRef(null);
  const mouseRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const container = mountRef.current;
    
    // --- THREE.JS SETUP ---
    // Scene & Camera
    const scene = new THREE.Scene();
    scene.fog = new THREE.FogExp2(0x050505, 0.002); // Soft fade into black

    const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
    camera.position.z = 30;

    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    container.appendChild(renderer.domElement);

    // --- OBJECTS ---

    // 1. The Core (Geometric Wireframe)
    const geometryCore = new THREE.IcosahedronGeometry(8, 1);
    
    // We use WireframeGeometry to get the "vector lines" look rather than standard wireframe material
    const wireframeGeom = new THREE.WireframeGeometry(geometryCore);
    const materialCore = new THREE.LineBasicMaterial({ 
        color: 0xffffff, 
        transparent: true, 
        opacity: 0.15 
    });
    const coreLines = new THREE.LineSegments(wireframeGeom, materialCore);
    scene.add(coreLines);

    // 2. The Inner Chip (Solid Block)
    const geometryChip = new THREE.BoxGeometry(4, 4, 4);
    const edgesChip = new THREE.EdgesGeometry(geometryChip);
    const materialChip = new THREE.LineBasicMaterial({ color: 0xffffff });
    const chipLines = new THREE.LineSegments(edgesChip, materialChip);
    scene.add(chipLines);

    // 3. The Particle Cloud (Data Nodes)
    const particlesGeometry = new THREE.BufferGeometry();
    const particlesCount = 400;
    const posArray = new Float32Array(particlesCount * 3);

    for(let i = 0; i < particlesCount * 3; i++) {
        // Spread particles in a sphere around the core
        posArray[i] = (Math.random() - 0.5) * 60; 
    }

    particlesGeometry.setAttribute('position', new THREE.BufferAttribute(posArray, 3));
    const materialParticles = new THREE.PointsMaterial({
        size: 0.15,
        color: 0xaaaaaa,
        transparent: true,
        opacity: 0.8
    });

    const particlesMesh = new THREE.Points(particlesGeometry, materialParticles);
    scene.add(particlesMesh);

    // 4. Connecting Lines (Vector Aesthetic)
    // A second larger wireframe rotating opposite
    const geometryOuter = new THREE.IcosahedronGeometry(15, 0);
    const edgesOuter = new THREE.EdgesGeometry(geometryOuter);
    const materialOuter = new THREE.LineBasicMaterial({ 
        color: 0x444444, 
        transparent: true, 
        opacity: 0.3 
    });
    const outerLines = new THREE.LineSegments(edgesOuter, materialOuter);
    scene.add(outerLines);


    // --- INTERACTION ---
    const handleMouseMove = (event) => {
        mouseRef.current.x = (event.clientX / window.innerWidth) * 2 - 1;
        mouseRef.current.y = -(event.clientY / window.innerHeight) * 2 + 1;
    };

    document.addEventListener('mousemove', handleMouseMove);

    // Handle Resize
    const handleResize = () => {
        camera.aspect = window.innerWidth / window.innerHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(window.innerWidth, window.innerHeight);
    };

    window.addEventListener('resize', handleResize);

    // --- ANIMATION LOOP ---
    const clock = new THREE.Clock();
    let animationId;

    const animate = () => {
        const elapsedTime = clock.getElapsedTime();

        // Rotate Core
        coreLines.rotation.y = elapsedTime * 0.1;
        coreLines.rotation.x = elapsedTime * 0.05;

        // Rotate Chip (faster)
        chipLines.rotation.y = -elapsedTime * 0.2;
        chipLines.rotation.z = elapsedTime * 0.1;

        // Rotate Outer Grid
        outerLines.rotation.y = -elapsedTime * 0.05;

        // Rotate Particles
        particlesMesh.rotation.y = elapsedTime * 0.02;

        // Mouse Interaction (Lerp for smoothness)
        // We gently tilt the whole scene group or camera based on mouse
        const targetX = mouseRef.current.x * 2;
        const targetY = mouseRef.current.y * 2;
        
        camera.position.x += (targetX - camera.position.x) * 0.05;
        camera.position.y += (targetY - camera.position.y) * 0.05;
        camera.lookAt(scene.position);

        renderer.render(scene, camera);
        animationId = requestAnimationFrame(animate);
    };

    animate();

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('mousemove', handleMouseMove);
      cancelAnimationFrame(animationId);
      if (mountRef.current && renderer.domElement) {
        // Check if mountRef.current still exists before removing
        try {
            mountRef.current.removeChild(renderer.domElement);
        } catch (e) {
            console.error("Error removing renderer:", e);
        }
      }
      
      // Dispose geometries and materials
      geometryCore.dispose();
      wireframeGeom.dispose();
      materialCore.dispose();
      
      geometryChip.dispose();
      edgesChip.dispose();
      materialChip.dispose();
      
      particlesGeometry.dispose();
      materialParticles.dispose();
      
      geometryOuter.dispose();
      edgesOuter.dispose();
      materialOuter.dispose();
    };
  }, []);

  return <div ref={mountRef} style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1 }} />;
};

export default ThreeCanvas;
